import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker, { registerLocale } from 'react-datepicker';
import { shift } from '@floating-ui/react';
import { ko, enUS } from 'date-fns/locale';
import {
  BIRTH_DATE_MAX,
  BIRTH_DATE_MIN,
  extractBirthDateIso,
  formatBirthDateDigitsToDisplay,
  formatBirthDateIsoToDisplay,
  parseBirthDateDigits,
} from '../../sites/default/pages/report/utils/birthDateUtils';
import {
  dateToIso,
  extractDateIso,
  formatDateDigitsToDisplay,
  formatDateIsoToDisplay,
  isoToDate,
  parseDateDigits,
} from '../../utils/datePickerUtils';
import { normalizeAppLanguage } from '../../assets/utils/comm/languageConstants';
import 'react-datepicker/dist/react-datepicker.css';
import './appDatePicker.css';

registerLocale('ko', ko);
registerLocale('en', enUS);

/** body portal — overflow:hidden 부모에서 잘리지 않도록 */
export const APP_DATEPICKER_PORTAL_ID = 'app-datepicker-portal';

const POPPER_MODIFIERS = [shift({ padding: 12, crossAxis: true })];
const POPPER_PROPS = { strategy: 'fixed' };

const CalendarIcon = (
  <span className="app-datepicker-icon" aria-hidden="true">
    <svg viewBox="0 0 24 24" width="16" height="16" focusable="false">
      <path
        fill="currentColor"
        d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 16H5V9h14v11Z"
      />
    </svg>
  </span>
);

const AppDatePickerInput = forwardRef(function AppDatePickerInput(
  {
    id,
    className,
    text,
    placeholder,
    disabled,
    readOnly,
    onTextChange,
    onTextBlur,
    onClick,
    inputMode,
    autoComplete,
    ariaLabel,
  },
  ref,
) {
  return (
    <input
      ref={ref}
      id={id}
      type="text"
      className={className}
      value={text}
      placeholder={placeholder}
      inputMode={inputMode}
      autoComplete={autoComplete}
      aria-label={ariaLabel}
      disabled={disabled}
      readOnly={readOnly}
      onClick={onClick}
      onChange={onTextChange}
      onBlur={onTextBlur}
    />
  );
});

/**
 * 공통 react-datepicker
 * - birthDate: 생년월일(1900~오늘, YYYYMMDD 직접 입력)
 * - 일반: 미래 날짜 제한 없음
 */
export default function AppDatePicker({
  value,
  onChange,
  name,
  className = 'form-control',
  wrapperClassName = '',
  readOnly = false,
  disabled = false,
  id,
  placeholder,
  birthDate = false,
  minDate,
  maxDate,
  showIcon = true,
  compact = false,
  ariaLabel,
}) {
  const { t, i18n } = useTranslation();
  const pickerLocale = normalizeAppLanguage(i18n.language) === 'en' ? 'en' : 'ko';
  const resolvedPlaceholder = placeholder ?? t('components.common.datePlaceholder');
  const formatIsoToDisplay = birthDate ? formatBirthDateIsoToDisplay : formatDateIsoToDisplay;
  const formatDigitsToDisplay = birthDate ? formatBirthDateDigitsToDisplay : formatDateDigitsToDisplay;
  const parseDigits = birthDate ? parseBirthDateDigits : parseDateDigits;

  const isoValue = useMemo(() => {
    const extract = birthDate ? extractBirthDateIso : extractDateIso;
    return extract(value);
  }, [birthDate, value]);

  const selected = isoToDate(isoValue);
  const [text, setText] = useState(() => formatIsoToDisplay(isoValue));
  const nonInteractive = readOnly || disabled;

  const resolvedMinDate = birthDate ? BIRTH_DATE_MIN : minDate;
  const resolvedMaxDate = birthDate ? BIRTH_DATE_MAX : maxDate;

  const wrapperClasses = useMemo(() => {
    const parts = ['app-datepicker-wrapper'];
    if (compact) parts.push('app-datepicker-wrapper--compact');
    if (wrapperClassName) parts.push(wrapperClassName);
    return parts.join(' ');
  }, [compact, wrapperClassName]);

  useEffect(() => {
    const extract = birthDate ? extractBirthDateIso : extractDateIso;
    setText(formatIsoToDisplay(extract(value)));
  }, [value, birthDate, formatIsoToDisplay]);

  const commitIso = useCallback(
    (iso) => {
      onChange?.(iso || '');
      setText(formatIsoToDisplay(iso));
    },
    [onChange, formatIsoToDisplay],
  );

  const handleCalendarChange = (date) => {
    commitIso(date ? dateToIso(date) : '');
  };

  const handleTextChange = (e) => {
    const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 8);
    const nextText = formatDigitsToDisplay(digits);
    setText(nextText);

    if (!digits) {
      commitIso('');
      return;
    }
    if (digits.length === 8) {
      const iso = parseDigits(digits);
      if (iso) commitIso(iso);
    }
  };

  const handleTextBlur = () => {
    const digits = String(text || '').replace(/\D/g, '');
    if (!digits) {
      commitIso('');
      return;
    }
    const iso = parseDigits(digits);
    if (iso) {
      commitIso(iso);
      return;
    }
    setText(formatIsoToDisplay(isoValue));
  };

  return (
    <>
      {name ? <input type="hidden" name={name} value={isoValue} readOnly aria-hidden="true" /> : null}
      <DatePicker
        selected={selected}
        onChange={handleCalendarChange}
        locale={pickerLocale}
        dateFormat="yyyy.MM.dd"
        showYearDropdown
        showMonthDropdown
        scrollableYearDropdown
        yearDropdownItemNumber={birthDate ? 100 : 15}
        minDate={resolvedMinDate}
        maxDate={resolvedMaxDate}
        placeholderText={resolvedPlaceholder}
        disabled={nonInteractive}
        readOnly={readOnly}
        showIcon={showIcon}
        toggleCalendarOnIconClick={showIcon}
        icon={showIcon ? CalendarIcon : undefined}
        portalId={APP_DATEPICKER_PORTAL_ID}
        popperPlacement="bottom-start"
        popperModifiers={POPPER_MODIFIERS}
        popperProps={POPPER_PROPS}
        wrapperClassName={wrapperClasses}
        popperClassName="app-datepicker-popper"
        calendarClassName="app-datepicker-calendar"
        customInput={
          <AppDatePickerInput
            id={id}
            className={className}
            text={text}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            readOnly={readOnly}
            onTextChange={handleTextChange}
            onTextBlur={handleTextBlur}
            inputMode="numeric"
            autoComplete={birthDate ? 'bday' : 'off'}
            ariaLabel={ariaLabel}
          />
        }
      />
    </>
  );
}
